import React from "react";
import Layout from "Layout";
import { Grid, makeStyles, Typography, Paper, FormControl, Radio, RadioGroup, FormControlLabel, Button, Box, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import useAuth from "Hooks/useAuth";
import useApi from "Hooks/useApi";
import { withRouter } from "react-router";
import googleAnalytics from "fuction/googleAnalytics";
import isOnReservation from "fuction/isOnReservation";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
}));

// Snackbarの中身として使用するAlertコンポーネント
// Material-UI v4では、Snackbar内でAlertを使用するために、以下のようなヘルパーコンポーネントを定義するのが一般的です。
function Alert(props) {
  // severityプロパティで色とアイコンが設定されます (success, error, warning, info)
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function Condition(props) {
  const { request } = useApi();
  const auth = useAuth();
  const [current, setCurrent] = React.useState(0);
  const [release, setRelease] = React.useState(0);
  const [maxWords, setMaxWords] = React.useState(0);
  const [maxTweets, setMaxTweets] = React.useState(0);
  const [contractType, setContractType] = React.useState("");
  const [limitExceed, setLimitExceed] = React.useState({
    enable: true,
    remain: 0,
  });
  const [limitExceedEnableIni, setLimitExceedEnableIni] = React.useState(true); // 更新ボタン活性判定用変数
  const [isReservation, setIsReservation] = React.useState(false);

  const setLimitExceedEnable = (event) => {
    // MUI 4 の RadioGroup の onChange は選択された Radio の value を string として返す
    const stringValue = event.target.value;
    // 文字列をブール値に変換
    const booleanValue = stringValue === 'true';
    setLimitExceed({ ...limitExceed, enable: booleanValue });
  };

  // Snackbarの表示状態を管理するboolean値
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  // Snackbarに表示するメッセージを管理
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  // 増量オプション自動適用設定更新を予約する関数
  const processSubmission = () => {
    request({
      method: "PUT",
      url: `config/:account_id/account`,
      data: {
        exceed: limitExceed,
      },
    })
    .then((result) => {
      // 処理成功後、Snackbarの状態を更新
      setSnackbarMessage((limitExceed.enable ? "「増量オプションを適用する」" : "「増量オプションを適用しない」") + (isReservation ? "に戻しました" : "で予約しました"));
      setIsReservation(isOnReservation(contractType, result.data.exceed.enable, current, maxTweets, result.data.exceed.remain));
      setLimitExceedEnableIni(result.data.exceed.enable); // 「予約する」ボタン活性判定用変数更新
      setOpenSnackbar(true); // Snackbarを表示
    });
  };

  // Snackbarが閉じられたときのハンドラ
  const handleClose = (event, reason) => {
    // クリックアウェイ (画面のどこかをクリック)で閉じないようにする設定
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false); // Snackbarを非表示に
  };

  // TODO: スタータープラン廃止後は増量オプション使用回数表示部分を削除する
  const starterPlan = ["devel", "silver", "gold", "platinum", "diamond"];

  const classes = useStyles();

  React.useEffect(() => {
    // タイトル変更
    document.title = "Buzz Finder - 契約内容";
  }, []);

  React.useEffect(() => {
    googleAnalytics(props.location.pathname, auth.state.user.account_id)
  }, [props.location.pathname]);

  React.useEffect(() => {
    if (!auth.state.user.account_id) {
      return;
    }

    Promise.all([
      request({
        method: "POST",
        url: `config/:account_id/account`,
        data: {
          "attrs": [
            "max_words",
            "max_tweets",
            "max_current",
            "release",
            "contract_type",
            "exceed",
          ]
        },
      }),
    ]).then(([filterhorse]) => {
      setMaxWords(filterhorse.data.max_words);
      setMaxTweets(filterhorse.data.max_tweets);
      setCurrent(filterhorse.data.max_current);
      setRelease(filterhorse.data.release);
      setContractType(filterhorse.data.contract_type);
      setLimitExceed(filterhorse.data.exceed);
      setLimitExceedEnableIni(filterhorse.data.exceed.enable);
      setIsReservation(isOnReservation(filterhorse.data.contract_type, filterhorse.data.exceed.enable, filterhorse.data.max_current, filterhorse.data.max_tweets, filterhorse.data.exceed.remain));
    });
  }, [auth.state.user.account_id]);

  return (
    <Layout
      content={
        <Grid>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant="h6" color="primary">
              ご契約内容
            </Typography>

            <Grid container className={classes.wrapper} space={1}>
              <Typography>取得キーワード登録可能数：</Typography>
              <Typography>{maxWords}</Typography>
            </Grid>
            <Grid container className={classes.wrapper}>
              <Typography>月間取得可能投稿件数：</Typography>
              <Typography>{current.toLocaleString()} 件</Typography>
            </Grid>
            {/* TODO: スタータープラン廃止後は増量オプション使用回数表示部分を削除する */}
            {starterPlan.includes(contractType) && (
            <Grid container className={classes.wrapper}>
              <Typography>月間取得可能投稿件数の増量オプション：</Typography>
              <Typography>{release} 回</Typography>
            </Grid>
            )}
          </Paper>

          {!starterPlan.includes(contractType) && contractType !== "ruby" && (
          <>
          <Box my={4} /> {/* 区切りとして Box を使用 */}
          <Paper elevation={3} className={classes.paper}>
            <Typography variant="h6" color="primary">
              増量オプション適用予約
            </Typography>

            <Grid container className={classes.wrapper}>
              <Typography>取得可能投稿件数の増量オプション：</Typography>
              <Typography>{limitExceed.remain.toLocaleString()} 件</Typography>
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <FormControl
                component="fieldset"
                disabled={contractType === "ruby"}
              >
                <RadioGroup
                  value={limitExceed.enable}
                  onChange={setLimitExceedEnable}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={
                      (isReservation && limitExceedEnableIni)
                      ? "増量オプションを適用する（予約中）"
                      : "増量オプションを適用する"
                    }
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={
                      (isReservation && !limitExceedEnableIni)
                      ? "増量オプションを適用しない（予約中）"
                      : "増量オプションを適用しない"
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid container justifyContent="center" className={classes.wrapper}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                disabled={contractType === "ruby" || limitExceedEnableIni === limitExceed.enable}
                onClick={processSubmission}
              >
                予約する
              </Button>

            <Grid container justifyContent="center" className={classes.wrapper}>
              <Typography variant="body2">※予約の反映は 3 時 と 15 時 です。</Typography>
            </Grid>

            </Grid>
          </Paper>
          </>
          )}
          {/* --- Snackbarコンポーネント --- */}
          <Snackbar
            // Snackbarの表示を制御するboolean値
            open={openSnackbar}
            // 自動で閉じるまでの時間 (ミリ秒)。デフォルトは5000ms
            autoHideDuration={6000}
            // 閉じるイベントが発生したときのハンドラ
            onClose={handleClose}
            // 表示位置 (画面上部中央)
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            {/* Snackbarの中にAlertコンポーネントを配置してスタイルを適用 */}
            <Alert onClose={handleClose} severity="success">
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Grid>
      }
    />
  );
}
export default withRouter(Condition);
